import { canonicalRirOf } from './canonicalRir';
import type { LoggerDraft } from './loggerDraftTypes';

// Backfill optional fields on legacy persisted drafts so downstream
// reducers can rely on every key being present:
//   - loadedSides (1.45.0)
//   - isCardio + per-set duration/distanceM/avgHrBpm/calories (1.66.9)
// This is the single chokepoint for both AsyncStorage rehydration and
// the edit/repeat workflows — extracted from loggerNestedReducer.ts (the
// 'hydrate' case) when the reducer passed the 500-line budget.
//
// ⚠️ It is no longer only a backfill: it also carries two MIGRATIONS that
// change behaviour, and serving two callers with different trust in the data
// is exactly why the second one is opt-in.
//   - LG-28, the AMRAP reps seed (guarded on not-completed + the prescribed
//     flag) — hydrateDraftAmrapSplit.test.ts
//   - PE-22 fetta D, the effort seed (guarded on not-completed AND on the
//     caller asking for it) — hydrateDraftEffortSplit.test.ts
// The plain backfill cases stay guarded by loggerReducerGroupsFields.test.ts.
export function hydrateDraft(
  draft: LoggerDraft,
  opts: {
    /** ⛔ Opt-in, and it must STAY opt-in. See the effort-split block below:
     *  this migration reads a pending set's effort as a prescription, which
     *  is only true for a draft that came out of AsyncStorage. Turn it on for
     *  a freshly built edit draft and it will move a real measurement. */
    migrateLegacyEffortSeed?: boolean;
  } = {},
): LoggerDraft {
  const entries = draft.entries.map((e) => ({
    ...e,
    loadedSides: e.loadedSides ?? 1,
    isCardio: e.isCardio ?? false,
    isTimeBased: e.isTimeBased ?? false,
    // Migrate legacy persisted drafts (pre-single-column): honour the
    // user's prior visibility so a column doesn't surprise-appear
    // mid-workout on a resumed draft. Legacy on→on, off→off; brand-new
    // entries get the default-on from add_exercise / hydration.
    effortColumn:
      e.effortColumn ??
      ((e as { rpeColumn?: boolean; rirColumn?: boolean }).rpeColumn ===
        true ||
        (e as { rpeColumn?: boolean; rirColumn?: boolean }).rirColumn ===
          true),
    historyOpen: e.historyOpen ?? false,
    // `?? []`: a malformed/older persisted entry might lack the sets
    // array; default to empty rather than throwing in this normalisation
    // chokepoint (loadPersistedLoggerState routes every persisted draft
    // through here before any consumer reads it).
    sets: (e.sets ?? []).map((s) => {
      // LG-28 migration. A draft persisted by a PRE-fix build carries a
      // prescribed AMRAP with its floor already typed into the reps cell and
      // no `amrapFloor` key at all. Left alone it rehydrates verbatim and
      // reproduces the exact bug the fix closes — the athlete resumes the
      // session, sees "8" as a real value, does 8, confirms "to failure". The
      // window is real: the dev channel reinstalls builds onto devices that
      // may hold an in-flight session.
      //
      // Guarded on NOT completed. A completed set's reps are the athlete's
      // record and are never ours to blank; only a set still pending can be
      // assumed to be showing the seed rather than a performed count.
      const amrapPrescribed = s.amrapPrescribed ?? s.amrap ?? false;
      const storedReps = s.reps ?? '';
      const preFixAmrapSeed =
        amrapPrescribed === true &&
        s.amrapFloor === undefined &&
        storedReps.trim() !== '' &&
        (s.completedAt ?? null) === null;
      // PE-22 fetta D — the effort split, same shape as the AMRAP one above.
      // A draft persisted by a PRE-split build carries the plan's prescribed
      // effort inside `rpe`/`rir`, i.e. the MEASUREMENT fields: that is the
      // bug this slice closes. Resumed verbatim on the new build, the athlete
      // fails a set or lets a bout close itself and the coach's target is
      // saved as their judgment (prescribedEffortLeak.test.ts).
      //
      // ⛔ Guarded on NOT completed, the same rule the reps seed above obeys
      // and for the same reason: a completed set's effort is the athlete's
      // record and is never ours to move. Only a set still PENDING can be
      // assumed to be holding a seed — post-split nothing writes `rpe`/`rir`
      // on a pending set at all (the band writes them as it completes, and
      // the edit leg only touches an already-logged set), which also makes
      // this migration idempotent: run it twice and the second pass finds
      // nothing to lift.
      const storedEffortRir = (() => {
        if (!opts.migrateLegacyEffortSeed) return undefined;
        if ((s.completedAt ?? null) !== null) return undefined;
        if (s.rirPrescribed !== undefined) return undefined;
        return canonicalRirOf(s.rir, s.rpe) ?? undefined;
      })();
      return {
      ...s,
      // Core text fields: a persisted set that pre-dates a field (or
      // was written by a path that omitted it) must rehydrate with a
      // string, not undefined — SetRow reads `note.trim()` /
      // `rpe.trim()` unconditionally on render, so a missing key
      // crashes the Logger on cold boot (toxic-draft 9350821c).
      reps: preFixAmrapSeed ? '' : storedReps,
      weight: s.weight ?? '',
      unit: s.unit ?? 'kg',
      extraNegative: s.extraNegative ?? false,
      note: s.note ?? '',
      rpe: storedEffortRir === undefined ? (s.rpe ?? '') : '',
      rir: storedEffortRir === undefined ? (s.rir ?? '') : '',
      // The target the migration just lifted out of the measurement cell, so
      // it is carried rather than silently lost — the mirror of amrapFloor.
      ...(s.rirPrescribed !== undefined
        ? { rirPrescribed: s.rirPrescribed }
        : storedEffortRir !== undefined
          ? { rirPrescribed: storedEffortRir }
          : {}),
      completedAt: s.completedAt ?? null,
      fail: s.fail ?? false,
      prFlash: s.prFlash ?? false,
      loadedSides: s.loadedSides ?? 1,
      duration: s.duration ?? '',
      distanceM: s.distanceM ?? '',
      avgHrBpm: s.avgHrBpm ?? '',
      calories: s.calories ?? '',
      // AMRAP split (measurement-contract §5). Drafts persisted before the
      // split carry ONE `amrap` key and it meant the PRESCRIPTION — the
      // chip that writes the outcome has never shipped enabled, so no
      // stored draft can hold an athlete-declared one. Re-read a pre-split
      // set's flag as the prescription and start its outcome clean:
      // otherwise an in-flight session resumed on the new build would save
      // "went to the limit" on every set the plan merely asked it of. The
      // presence of `amrapPrescribed` is what marks a post-split draft.
      amrapPrescribed,
      amrap: s.amrapPrescribed === undefined ? false : (s.amrap ?? false),
      // The seed the migration above just lifted out of the reps cell, so the
      // floor is still shown ("AMRAP ≥ 8") instead of silently lost.
      ...(preFixAmrapSeed ? { amrapFloor: storedReps } : {}),
      // Slice 4 backfill: persisted dropset steps may pre-date the
      // completedAt field. Treat-as-pending on rehydration so the
      // pill UI starts at ◯; cycling stamps the timestamp going
      // forward. (Saved-history render at the workout-list layer
      // treats `completedAt === undefined` as ✓ per spec §8.)
      dropset:
        s.dropset === null || s.dropset === undefined
          ? null
          : {
              ...s.dropset,
              steps: s.dropset.steps.map((step) => ({
                ...step,
                completedAt: step.completedAt ?? null,
              })),
            },
      };
    }),
  }));
  return { ...draft, entries };
}
